// ============================================================================
// Session — which org this tab is working in
// ----------------------------------------------------------------------------
// The identity provider says who someone is. It cannot say which org they are
// working in, because a person may belong to several and the provider knows
// nothing about ours. So after sign-in the app exchanges the ID token for a
// session token at POST /api/session/tenant, and every other request carries
// that (ADR 0007). The server chooses the org and checks the membership; this
// file only asks.
//
// Which org to ask for:
//
//   - the one this tab last switched to, if any (sessionStorage, so a reload
//     stays put but a new tab follows the hostname);
//   - otherwise nothing, and the server uses the hostname or the first
//     membership.
//
// The session token lives in memory only. It is short-lived and re-minted
// from the ID token whenever it lapses, so there is nothing worth persisting.
//
// Switching reloads the page. That is deliberate: the query cache, component
// state and any open question all belong to the previous org, and a reload is
// the one way to be certain none of it is still on screen after the switch —
// the failure that matters once a person can hold two orgs is not leakage
// across a boundary but leakage across a switch.
// ============================================================================

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { ApiError, mintSession, setSessionHandlers } from "@/lib/api";
import type { MintedSession } from "@/lib/api";
import type { Membership } from "@/types";

// Exported for lib/session-handoff.ts, which hands it to a new tab with the tokens.
export const ORG_PREFERENCE_KEY = "2dai.session.org";
const SSO_REDIRECT_KEY = "2dai.session.sso-redirect";

/**
 * Sign in again through the provider an org requires. Once per tab and
 * provider, so an IdP that signs someone in who still has no access shows an
 * error rather than bouncing forever. Returns false when there is nothing
 * left to try.
 */
function followSSO(provider: string | undefined, signIn: (provider?: string) => void): boolean {
  if (!provider) return false;
  try {
    if (window.sessionStorage.getItem(SSO_REDIRECT_KEY) === provider) return false;
    window.sessionStorage.setItem(SSO_REDIRECT_KEY, provider);
  } catch {
    return false;
  }
  signIn(provider);
  return true;
}
function readPreference(): string | undefined {
  try {
    return window.sessionStorage.getItem(ORG_PREFERENCE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function writePreference(orgId: string | null): void {
  try {
    if (orgId) window.sessionStorage.setItem(ORG_PREFERENCE_KEY, orgId);
    else window.sessionStorage.removeItem(ORG_PREFERENCE_KEY);
  } catch {
    /* a preference, not state: losing it lands the tab on the default org */
  }
}

export interface SessionState {
  isLoading: boolean;
  error: Error | null;
  session: MintedSession | null;
  /**
   * Move to another org: a reload into it, or a sign-in through the provider
   * it requires. The server refuses an org the person is not in.
   */
  switchOrg: (membership: Membership) => void;
}

const SessionContext = createContext<SessionState | null>(null);

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession was called outside SessionProvider");
  return ctx;
}

export function SessionProvider({
  idToken,
  signIn,
  children,
}: {
  idToken: string;
  signIn: (provider?: string) => void;
  children: ReactNode;
}) {
  const [session, setSession] = useState<MintedSession | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // Refs, because the refresher is called from plain functions in api.ts long
  // after this render: it must see the ID token react-oidc-context renewed in
  // the background, and the org this tab is actually in.
  const idTokenRef = useRef(idToken);
  const sessionRef = useRef<MintedSession | null>(null);
  const inflight = useRef<Promise<string | null> | null>(null);

  useEffect(() => {
    idTokenRef.current = idToken;
  }, [idToken]);

  const mint = useCallback(async (orgId?: string): Promise<MintedSession> => {
    try {
      return await mintSession(idTokenRef.current, orgId);
    } catch (e) {
      // A remembered org the person has since been removed from is not an
      // error worth showing: forget it and let the server choose.
      if (orgId && e instanceof ApiError && e.needsInvitation) {
        writePreference(null);
        return mintSession(idTokenRef.current);
      }
      throw e;
    }
  }, []);

  const adopt = useCallback((s: MintedSession) => {
    // A refresh can come back for a different org — the membership this tab
    // was in was revoked, and the server chose another. Everything on screen
    // belongs to the old one, so this is a switch, and a switch reloads.
    if (sessionRef.current && sessionRef.current.org.id !== s.org.id) {
      writePreference(null);
      window.location.assign("/");
      return;
    }
    sessionRef.current = s;
    setSession(s);
    setError(null);
    try {
      window.sessionStorage.removeItem(SSO_REDIRECT_KEY);
    } catch {
      /* nothing to clear */
    }
  }, []);

  // One refresh at a time. A page that fires six queries at an expired session
  // gets six 401s, and should mint once, not six times.
  const refresh = useCallback((): Promise<string | null> => {
    if (!inflight.current) {
      inflight.current = mint(sessionRef.current?.org.id ?? readPreference())
        .then((s) => {
          adopt(s);
          return s.token;
        })
        .catch((e: Error) => {
          if (e instanceof ApiError && e.needsSSO && followSSO(e.details.provider, signIn)) {
            return null; // signing in again through the org's provider
          }
          // Tear the app down only for an answer that will not improve by
          // itself: no session at all yet, or the server refusing the
          // identity. A dropped connection mid-session leaves the page up and
          // lets the failing request report its own error.
          const refused = e instanceof ApiError && (e.status === 401 || e.status === 403);
          if (!sessionRef.current || refused) setError(e);
          return null;
        })
        .finally(() => {
          inflight.current = null;
        });
    }
    return inflight.current;
  }, [adopt, mint, signIn]);

  useEffect(() => {
    setSessionHandlers(() => sessionRef.current?.token ?? null, refresh);
    return () => setSessionHandlers(() => null, null);
  }, [refresh]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const switchOrg = useCallback(
    (m: Membership) => {
      if (m.orgId === sessionRef.current?.org.id) return;
      writePreference(m.orgId);
      if (!m.usable && m.ssoProvider) {
        // Signing in through that provider makes a different Cognito user, so
        // what comes back is that identity's memberships — including this org.
        signIn(m.ssoProvider);
        return;
      }
      window.location.assign("/");
    },
    [signIn],
  );

  const value = useMemo<SessionState>(
    () => ({ isLoading: !session && !error, error, session, switchOrg }),
    [session, error, switchOrg],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
